/**
 * Пределы личных размеров предмета каталога.
 *
 * Менять размер можно у любого предмета раздела, где это разрешено, — пользователь
 * не должен гадать, почему у кровати глубина меняется, а у дивана нет. Пределы
 * широкие и одинаковые для всех: от четверти до четырёх каталожных размеров. Раньше
 * модель файлом держали в коридоре ±20 %, чтобы растяжение было незаметно, но людям
 * нужно поставить ровно ту вещь, что у них есть, — шкаф на всю нишу, узкую тумбу.
 * Свои пределы в каталоге (`sizeLimits`) теперь могут только расширить общий коридор
 * (ковёр до 5 м), но не сузить его.
 */
import type { CatalogItem, SizeAxis, SizeRange } from "./types";

/** Во сколько раз можно уменьшить и увеличить предмет по одной оси. */
const STRETCH = { min: 0.25, max: 4 };
/** Абсолютные границы любого размера, мм: тоньше — не разглядеть, больше — не квартира. */
const ABSOLUTE = { min: 50, max: 10000 };
/** Шаг, к которому округляются пределы и размеры, мм. */
export const SIZE_STEP = 10;

const toStep = (value: number) => Math.round(value / SIZE_STEP) * SIZE_STEP;

/** Пределы размера по оси: общий коридор, расширенный своими пределами из каталога. */
export const itemSizeRange = (item: CatalogItem, axis: SizeAxis): SizeRange => {
	const base = item[axis];
	const min = Math.max(ABSOLUTE.min, toStep(base * STRETCH.min));
	const max = Math.min(ABSOLUTE.max, Math.max(toStep(base * STRETCH.max), base));
	const own = item.sizeLimits?.[axis];
	return own ? { min: Math.min(min, own.min), max: Math.max(max, own.max) } : { min, max };
};

/** Размер, приведённый к шагу и пределам предмета. */
export const clampItemSize = (item: CatalogItem, axis: SizeAxis, value: number): number => {
	const { min, max } = itemSizeRange(item, axis);
	return Math.min(max, Math.max(min, toStep(value)));
};
